import React, { useMemo } from "react";
import { Card, CardBody, CardHeader, Col, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";

import {
  AXIS_LABEL_STYLE,
  AXIS_TITLE_STYLE,
  formatBucket,
  themeColor,
} from "./format";
import { ActivityReportData } from "./types";

const CHART_ID = "activity-report-trend";

/**
 * Visits and distinct users over the selected window. The bucket size follows
 * the length of that window, so the same chart serves a week and five years.
 * Users sit on their own scale: on a shared axis a few dozen people flatten
 * into a line along zero beneath hundreds of visits.
 */
const UsageTrend = ({
  data,
  loading,
}: {
  data: ActivityReportData | null;
  loading: boolean;
}) => {
  const points = useMemo(() => data?.trend?.points || [], [data]);
  const granularity = data?.trend?.granularity || "day";
  const hasActivity = points.some((point) => point.visits > 0);

  const categories = useMemo(
    () => points.map((point) => formatBucket(point.date, granularity)),
    [points, granularity]
  );

  const series = useMemo(
    () => [
      { name: "Page visits", data: points.map((point) => point.visits) },
      { name: "Active users", data: points.map((point) => point.users) },
    ],
    [points]
  );

  const wholeNumber = (value: number) => String(Math.round(value));

  // Colours are read from the theme at render; re-render on a light/dark
  // switch so the chart follows it.
  useSelector((state: any) => state.Layout?.layoutModeType);

  // Two series that must never be mistaken for each other: they differ in
  // hue, lightness, line pattern and marker shape, so colour is not the only
  // cue (and no status hue is spent on plain data).
  const visitsColour = themeColor("--vz-primary", "#3577f1");
  const usersColour = themeColor("--cims-series-2", "#dd6b20");
  // Axis text in the accessible ink of each series, so each scale is
  // matched to its line at a glance.
  const visitsInk = themeColor("--cims-ink-primary", "#2859b5");

  const options: any = {
    chart: {
      height: 320,
      type: "line",
      zoom: { enabled: false },
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors: [visitsColour, usersColour],
    dataLabels: { enabled: false },
    // Straight segments: a smoothed curve invents values between sparse points.
    // Visits solid, users dashed.
    stroke: { width: [2.5, 2], curve: "straight", dashArray: [0, 6] },
    markers: {
      size: points.length <= 31 ? 4 : 0,
      shape: ["circle", "square"],
      strokeWidth: 0,
      hover: { sizeOffset: 2 },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "13px",
      markers: { shape: ["circle", "square"] },
    },
    xaxis: {
      categories,
      tickAmount: Math.min(categories.length, 12),
      labels: { style: AXIS_LABEL_STYLE },
    },
    yaxis: [
      {
        seriesName: "Page visits",
        min: 0,
        title: {
          text: "Page visits",
          style: { ...AXIS_TITLE_STYLE, color: visitsInk },
        },
        labels: {
          formatter: wholeNumber,
          style: { ...AXIS_LABEL_STYLE, colors: visitsInk },
        },
      },
      {
        seriesName: "Active users",
        opposite: true,
        min: 0,
        title: {
          text: "Active users (dashed)",
          style: { ...AXIS_TITLE_STYLE, color: usersColour },
        },
        labels: {
          formatter: wholeNumber,
          style: { ...AXIS_LABEL_STYLE, colors: usersColour },
        },
      },
    ],
    tooltip: { shared: true, intersect: false },
  };

  return (
    <Col xxl={8}>
      <Card className="card-height-100 usage-trend">
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">Usage over time</h4>
          <span className="text-ink-muted fs-12">By {granularity}</span>
        </CardHeader>

        <CardBody className="pb-2">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="sm" />
            </div>
          ) : !hasActivity ? (
            <div className="text-center text-ink-muted py-5">
              No activity in this period.
            </div>
          ) : (
            <div
              role="img"
              aria-label={`Line chart of page visits and active users by ${granularity}: ${points
                .reduce((sum, point) => sum + point.visits, 0)
                .toLocaleString()} visits in total, at most ${Math.max(
                ...points.map((point) => point.users)
              )} active users in one ${granularity}.`}
            >
              <ReactApexChart
                dir="ltr"
                options={options}
                series={series}
                id={CHART_ID}
                type="line"
                height="320"
                className="apex-charts"
              />
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default UsageTrend;
